import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import { PaginatedResponse, PressReleaseLight } from '@finance-lab/utils/types'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { PressReleaseApi } from '../../../api/PressReleasesApi'
import { GridColDef } from '@mui/x-data-grid/models'
import { formatDateYYYYMMDDhhmm } from '../../../packages/utils/numberFormatters'
import { PressReleaseAvatar } from '../../../components/entities/PressReleaseAvatar'
import { PressReleaseRelatedTransactionsTags } from '../../../components/entities/PressReleaseRelatedTransactionsTags'
import { GridApiPro } from '@mui/x-data-grid-pro/models'
import { Link, useNavigate } from 'react-router-dom'
import { getPressReleaseLink } from '../../../utils/get-press-release-link'
import { flagPath } from '../../../components/entities/CompaniesGrid'
import { PressReleasesFilter } from '../../components/press-releases/PressReleasesFilter'
import { useFilterSettings } from '../../components/press-releases/utils'
import { normalizedName } from '../../../components/entities/CompanyName'

const pressReleaseApi = new PressReleaseApi()

export const PressReleasesView = () => {
    const navigate = useNavigate()
    const [pageInfo, setPageInfo] = useState<PaginatedResponse<Omit<PressReleaseLight, 'body'>>>({
        totalCount: 0,
        data: [],
    })
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 0,
    })
    const apiRef = useRef<GridApiPro | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const { endDate, searchValue, contentType } = useFilterSettings()

    useEffect(() => {
        const fetchData = async () => {
            const { data, totalCount } = await pressReleaseApi.allPressReleases(paginationModel, {
                publicationDate: endDate ?? undefined,
                contentType,
                search: searchValue,
            })

            setPageInfo({ data: data.map(({ body, ...pr }) => pr), totalCount })
            setIsLoading(false)

            await Promise.resolve()
        }

        void fetchData()
        setIsLoading(true)
    }, [contentType, endDate, paginationModel, searchValue])

    const columns: GridColDef<Omit<PressReleaseLight, 'body'>>[] = [
        {
            field: 'avatar',
            display: 'flex',
            align: 'left',
            sortable: false,
            width: 154,
            headerName: '',
            renderCell: ({ row }) => {
                const langs = [
                    { prId: row.id, language: row.language },
                    ...row.translations,
                ] as PressReleaseLight['translations']

                return (
                    <>
                        <PressReleaseAvatar pressRelease={row} size={'small'} />
                        {langs
                            .sort((a, b) => a.language.localeCompare(b.language))
                            .map((lg) => (
                                <Link key={lg.prId} to={getPressReleaseLink(row.companyId, lg.prId)}>
                                    <img
                                        src={flagPath(
                                            lg.language === 'en' ? 'gb' : lg.language == 'sv' ? 'se' : lg.language,
                                        )}
                                        width={25}
                                        style={{ marginLeft: 14 }}
                                    />
                                </Link>
                            ))}
                    </>
                )
            },
        },
        {
            field: 'companyName',
            renderCell: ({ row }) => <Link to={`/companies/${row.companyId}`}>{normalizedName(row.companyName)}</Link>,
            headerName: 'Bolag',
            minWidth: 150,
        },
        {
            field: 'relatedTransactions',
            display: 'flex',
            align: 'left',
            sortable: false,
            headerName: '',
            renderCell: ({ row }) => {
                return <PressReleaseRelatedTransactionsTags relatedTransactions={row.relatedTransactions} />
            },
        },
        {
            headerName: 'Tidpunkt',
            field: 'publishedAt',
            type: 'dateTime',
            minWidth: 160,
            valueFormatter: (v) => formatDateYYYYMMDDhhmm(v),
        },
        {
            headerName: 'Rubrik',
            field: 'title',
            minWidth: 360,
            flex: 1,
        },
    ]

    return (
        <>
            <Helmet title='Pressmeddelanden' />
            <Typography variant='h3' gutterBottom display='block'>
                Pressmeddelanden
            </Typography>

            <PressReleasesFilter isSingleDaySelect />

            <Divider sx={{ marginY: 4 }} />

            <DataGridPro
                autosizeOnMount
                autosizeOptions={{
                    columns: ['relatedTransactions', 'avatar'],
                }}
                apiRef={apiRef}
                columns={columns}
                filterMode='server'
                loading={isLoading}
                onPaginationModelChange={setPaginationModel}
                onCellClick={(params, event) => {
                    if (params.colDef.field === 'relatedTransactions' || params.colDef.field === 'avatar') return

                    let link = null
                    if (params.colDef.field === 'companyName') {
                        link = `/companies/${params.row.companyId}`
                    } else {
                        link = getPressReleaseLink(params.row.companyId, params.row.id)
                    }

                    if (!link) return

                    if (event.ctrlKey || event.metaKey) {
                        window.open(link, '_blank')
                    } else {
                        void navigate(link)
                    }
                }}
                paginationModel={paginationModel}
                paginationMode='server'
                rows={pageInfo.data}
                rowCount={pageInfo.totalCount}
                sx={{
                    '.MuiDataGrid-row': {
                        cursor: 'pointer',
                        ':hover': {},
                    },
                }}
            />
        </>
    )
}
